import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import ClientBillsPanel from './finance/ClientBills';
import { apiGet } from '../lib/api';
import { inrCompact, formatDate, statusColor, inr } from '../lib/format';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ComposedChart, Cell,
} from 'recharts';
import {
  TrendingDown, TrendingUp, AlertCircle, Package, Users,
  Wrench, Wallet, Fuel, ArrowRight, CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';
import { useUrlTab } from '../lib/hooks/useUrlState';

const TABS = ['Overview', 'Portfolio P&L', 'Project P&L', 'AP Aging', 'AR Aging', '13-Week Cash Flow', 'Client RA Bills'];

export default function Finance() {
  const [tab, setTab] = useUrlTab('tab', 'Overview');
  const [projects, setProjects] = useState([]);
  const [aging, setAging] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [pid, setPid] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    apiGet('/projects').then(p => { setProjects(p); if (p[0]) setPid(p[0].id); });
    apiGet('/finance/aging').then(setAging);
    apiGet('/finance/cashflow-forecast').then(setForecast);
    apiGet('/finance/portfolio-pnl').then(setPortfolio);
  }, []);
  useEffect(() => { if (pid) apiGet(`/finance/pnl/${pid}`).then(setPnl); }, [pid]);

  return (
    <div>
      <PageHeader overline="Finance & Accounting" title="CFO Command Center"
        subtitle="Project P&L, accounts payable / receivable aging, and 13-week rolling cash flow forecast." />

      {/* Top 4 tiles */}
      {aging && forecast && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KCard label="Cash on Hand" value={inrCompact(forecast.opening_balance)} sub="Opening balance · today" icon={Wallet} accent="text-[#3b4872] bg-indigo-50" />
          <KCard label="Total AP Outstanding" value={inrCompact(aging.ap.total)} icon={TrendingDown}
            accent={(aging.ap.by_category?.['materials']?.buckets?.['90+'] || 0) > 0 ? 'text-red-700 bg-red-50' : 'text-orange-700 bg-orange-50'}
            sub={aging.ap.by_category?.['materials'] ? `Materials ₹${inrCompact(aging.ap.by_category['materials'].total)}` : ''} />
          <KCard label="Total AR Outstanding" value={inrCompact(aging.ar.total)} icon={TrendingUp} accent="text-emerald-700 bg-emerald-50"
            sub={aging.ar.by_category?.['ra_bills'] ? `RA Bills ₹${inrCompact(aging.ar.by_category['ra_bills'].total)}` : ''} />
          <KCard label="13-wk Net Cash Flow" value={inrCompact(forecast.weeks.reduce((s, w) => s + w.net, 0))} sub="Projected position" icon={TrendingUp} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`fin-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#3b4872]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Overview' && <Overview aging={aging} forecast={forecast} portfolio={portfolio} navigate={navigate} />}
          {tab === 'Portfolio P&L' && <PortfolioPnLPanel portfolio={portfolio} navigate={navigate} />}
          {tab === 'Project P&L' && <PnLPanel projects={projects} pid={pid} setPid={setPid} pnl={pnl} />}
          {tab === 'AP Aging' && <AgingPanel kind="ap" data={aging?.ap} title="Accounts Payable" />}
          {tab === 'AR Aging' && <AgingPanel kind="ar" data={aging?.ar} title="Accounts Receivable" />}
          {tab === '13-Week Cash Flow' && <ForecastPanel forecast={forecast} />}
          {tab === 'Client RA Bills' && <ClientBillsPanel projects={projects} />}
        </div>
      </div>
    </div>
  );
}

function KCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="kpi-overline">{label}</div>
        {Icon && <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}><Icon size={13} /></div>}
      </div>
      <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

// ── Category mini-bar (horizontal stacked aging) ────────────────────────────
const BUCKET_COLORS = { '0-30': '#10B981', '31-60': '#F59E0B', '61-90': '#6366F1', '90+': '#EF4444' };
const CAT_ICONS = { materials: Package, labour: Users, fixed_costs: Wrench, other: Wallet, ra_bills: TrendingUp, milestone_bills: CheckCircle2, retention: Wallet };

function CategoryBreakdown({ data, label }) {
  if (!data) return null;
  const categories = Object.entries(data);
  if (categories.length === 0) return <div className="text-xs text-slate-400 py-4">No outstanding items</div>;
  const max = Math.max(...categories.map(([, v]) => v.total), 1);

  return (
    <div className="space-y-3">
      <div className="kpi-overline mb-1">{label}</div>
      {categories.map(([cat, info]) => {
        const Icon = CAT_ICONS[cat] || Wallet;
        const totalBuckets = Object.values(info.buckets).reduce((s, v) => s + v, 0) || 1;
        return (
          <div key={cat}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 capitalize">
                <Icon size={12} className="text-slate-400" />
                {cat.replace(/_/g, ' ')}
              </div>
              <div className="text-xs font-display font-bold tabular-nums">{inrCompact(info.total)}</div>
            </div>
            <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
              {Object.entries(info.buckets).map(([b, v]) => (
                v > 0 ? (
                  <div key={b} className="h-full transition-all"
                    style={{ width: `${(v / totalBuckets) * 100}%`, backgroundColor: BUCKET_COLORS[b] }}
                    title={`${b} days: ${inrCompact(v)}`} />
                ) : null
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex gap-3 mt-2">
        {Object.entries(BUCKET_COLORS).map(([b, c]) => (
          <div key={b} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: c }} />
            <span className="text-[10px] text-slate-500">{b}d</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Overview ─────────────────────────────────────────────────────────────────
function Overview({ aging, forecast, portfolio, navigate }) {
  if (!aging || !forecast) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;

  return (
    <div className="space-y-6">
      {/* Row 1: AP/AR by category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <CategoryBreakdown data={aging.ap.by_category} label="Payables by Category" />
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <CategoryBreakdown data={aging.ar.by_category} label="Receivables by Category" />
        </div>
      </div>

      {/* Row 2: Portfolio P&L Snapshot */}
      {portfolio && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="kpi-overline">Project P&L Snapshot — all active projects</div>
            <button onClick={() => navigate ? undefined : null}
              className="text-xs font-semibold text-[#3b4872] flex items-center gap-1">
              View full table <ArrowRight size={12} />
            </button>
          </div>
          <PortfolioTable projects={portfolio.projects.slice(0, 6)} />
        </div>
      )}

      {/* Row 3: AP/AR aging bar charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="kpi-overline mb-2">AP Aging Buckets</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={Object.entries(aging.ap.buckets).map(([b, v]) => ({ bucket: b, AP: v }))}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="bucket" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `${(v / 10000000).toFixed(0)}Cr`} />
              <Tooltip cursor={{ fill: 'rgba(255,87,34,0.06)' }} contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} formatter={v => inrCompact(v)} />
              <Bar dataKey="AP" radius={[2, 2, 0, 0]}>
                {Object.entries(aging.ap.buckets).map(([b]) => <Cell key={b} fill={BUCKET_COLORS[b]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div className="kpi-overline mb-2">AR Aging Buckets</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={Object.entries(aging.ar.buckets).map(([b, v]) => ({ bucket: b, AR: v }))}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="bucket" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `${(v / 10000000).toFixed(0)}Cr`} />
              <Tooltip cursor={{ fill: 'rgba(16,185,129,0.06)' }} contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} formatter={v => inrCompact(v)} />
              <Bar dataKey="AR" fill="#10B981" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 4: Cash flow preview */}
      <div>
        <div className="kpi-overline mb-2">Cash Flow Forecast — 13 weeks</div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={forecast.weeks}>
            <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="week" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `${(v / 10000000).toFixed(0)}Cr`} />
            <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} formatter={v => inrCompact(v)} />
            <Bar dataKey="inflow" fill="#10B981" name="Inflow" radius={[2, 2, 0, 0]} />
            <Bar dataKey="outflow" fill="#EF4444" name="Outflow" radius={[2, 2, 0, 0]} />
            <Line type="monotone" dataKey="cumulative" stroke="#3b4872" strokeWidth={2} dot={{ r: 3, fill: '#3b4872' }} name="Cumulative cash" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Portfolio P&L Panel ───────────────────────────────────────────────────────
function HealthBadge({ health }) {
  if (health === 'green') return <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 size={12} /> On track</span>;
  if (health === 'amber') return <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-semibold"><AlertTriangle size={12} /> At risk</span>;
  return <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><XCircle size={12} /> Critical</span>;
}

function EVMBadge({ value, label }) {
  if (value == null) return <span className="text-slate-400 text-xs">—</span>;
  const cls = value >= 1.0 ? 'text-emerald-700' : value >= 0.9 ? 'text-amber-600' : 'text-red-600';
  return <span className={`font-display font-bold text-xs tabular-nums ${cls}`}>{value.toFixed(2)}</span>;
}

function PortfolioTable({ projects }) {
  if (!projects || projects.length === 0) {
    return <div className="text-center py-8 text-slate-400 text-sm">No active projects found</div>;
  }
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="portfolio-pnl-table">
      <table className="w-full dense-table text-xs">
        <thead>
          <tr>
            <th>Project</th>
            <th className="text-right">Contract Value</th>
            <th className="text-right">Earned Revenue</th>
            <th className="text-right">Total Cost</th>
            <th className="text-right">Gross Profit</th>
            <th className="text-right">Margin %</th>
            <th className="text-right">CPI</th>
            <th className="text-right">SPI</th>
            <th>Health</th>
          </tr>
        </thead>
        <tbody>
          {projects.map(p => (
            <tr key={p.project_id} className="cursor-pointer hover:bg-slate-50"
              onClick={() => window.location.href = `/app/projects/${p.project_id}`}
              data-testid={`portfolio-row-${p.project_id}`}>
              <td>
                <div className="font-semibold text-[#3b4872]">{p.project_code}</div>
                <div className="text-[10px] text-slate-500 truncate max-w-[160px]">{p.project_name}</div>
              </td>
              <td className="text-right tabular-nums font-mono">{inrCompact(p.contract_value)}</td>
              <td className="text-right tabular-nums font-mono">{inrCompact(p.earned_revenue)}</td>
              <td className="text-right tabular-nums font-mono text-red-600">{inrCompact(p.total_cost)}</td>
              <td className={`text-right tabular-nums font-display font-bold ${p.gross_profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{inrCompact(p.gross_profit)}</td>
              <td className={`text-right tabular-nums font-display font-bold ${p.gross_margin_pct >= 15 ? 'text-emerald-700' : p.gross_margin_pct >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{p.gross_margin_pct}%</td>
              <td className="text-right"><EVMBadge value={p.CPI} /></td>
              <td className="text-right"><EVMBadge value={p.SPI} /></td>
              <td><HealthBadge health={p.health} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PortfolioPnLPanel({ portfolio }) {
  if (!portfolio) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  const t = portfolio.totals || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KCard label="Contract Value" value={inrCompact(t.total_contract_value)} />
        <KCard label="Earned Revenue" value={inrCompact(t.total_earned_revenue)} />
        <KCard label="Total Cost" value={inrCompact(t.total_cost)} accent="text-red-700 bg-red-50" />
        <KCard label="Total Profit" value={inrCompact(t.total_profit)} accent={t.total_profit >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'} />
        <KCard label="Avg Margin" value={`${t.avg_margin_pct}%`} accent={t.avg_margin_pct >= 15 ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'} />
      </div>
      <PortfolioTable projects={portfolio.projects} />
    </div>
  );
}

// ── Project P&L Panel (comprehensive) ────────────────────────────────────────
const COST_ICONS = { procurement: Package, material: Package, labour: Users, payroll: Users, subcontractor: Wrench, petty_cash: Wallet, fuel: Fuel };
const COST_LABELS = { procurement: 'Procurement (GRN)', material: 'Materials Received', labour: 'Direct Labour', payroll: 'Office Payroll', subcontractor: 'Subcontractor Bills', petty_cash: 'Petty Cash', fuel: 'Fuel' };

function EVMCard({ evm }) {
  if (!evm) return null;
  const metrics = [
    { key: 'BAC', label: 'Budget at Completion', format: inrCompact },
    { key: 'EV', label: 'Earned Value', format: inrCompact },
    { key: 'AC', label: 'Actual Cost', format: inrCompact },
    { key: 'CPI', label: 'Cost Perf. Index', format: v => v?.toFixed(2) ?? '—', health: true },
    { key: 'SPI', label: 'Schedule Perf. Index', format: v => v?.toFixed(2) ?? '—', health: true },
    { key: 'EAC', label: 'Est. at Completion', format: inrCompact },
    { key: 'ETC', label: 'Est. to Complete', format: inrCompact },
    { key: 'VAC', label: 'Variance at Completion', format: inrCompact, signed: true },
    { key: 'CV', label: 'Cost Variance', format: inrCompact, signed: true },
    { key: 'SV', label: 'Schedule Variance', format: inrCompact, signed: true },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="kpi-overline">Earned Value Management (EVM)</div>
        <div className="text-xs text-slate-500 mt-0.5">Status: <span className={`font-semibold ${evm.status === 'on_track' ? 'text-emerald-700' : evm.status === 'at_risk' ? 'text-amber-600' : 'text-red-600'}`}>{(evm.status || '').replace('_', ' ')}</span></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-100">
        {metrics.map(({ key, label, format, health, signed }) => {
          const val = evm[key];
          let color = '';
          if (health && val != null) {
            color = val >= 1.0 ? 'text-emerald-700' : val >= 0.9 ? 'text-amber-600' : 'text-red-600';
          } else if (signed && val != null) {
            color = val >= 0 ? 'text-emerald-700' : 'text-red-600';
          }
          return (
            <div key={key} className="bg-white px-3 py-2.5" data-testid={`evm-${key.toLowerCase()}`}>
              <div className="text-[10px] text-slate-400 font-display uppercase tracking-wider">{key}</div>
              <div className={`font-display font-extrabold text-sm tabular-nums mt-0.5 ${color}`}>
                {val != null ? format(val) : '—'}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PnLPanel({ projects, pid, setPid, pnl }) {
  if (!pnl) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  const rev = pnl.revenue || {};
  const costs = pnl.costs || {};
  const margin = pnl.margin || {};
  const budgetData = (pnl.budgets || []).map(b => ({
    head: b.head, Budgeted: b.Budgeted || b.budgeted || 0, Actual: b.Actual || b.actual || 0,
  }));

  return (
    <div className="space-y-5">
      {/* Project selector */}
      <div className="flex items-center gap-3">
        <div className="kpi-overline shrink-0">Project</div>
        <select data-testid="pnl-project-select" value={pid} onChange={e => setPid(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white max-w-xs">
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
        </select>
      </div>

      {/* Revenue + Costs grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Revenue card */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-100 kpi-overline">Revenue</div>
          <div className="divide-y divide-slate-100">
            {[
              { label: 'Earned Revenue (Contract × Progress)', value: rev.earned_revenue },
              { label: 'RA Bills Raised', value: rev.ra_bills_raised },
              { label: 'RA Bills Collected', value: rev.ra_bills_collected, accent: 'text-emerald-700' },
              { label: 'Pending Collection', value: rev.ra_bills_pending_collection, accent: 'text-amber-600' },
              { label: 'Retention Held', value: rev.retention_held, accent: 'text-red-600' },
              { label: 'TDS Deducted by Client', value: rev.tds_deducted_by_client, accent: 'text-red-600' },
            ].map(({ label, value, accent }) => (
              <div key={label} className="px-4 py-2.5 flex items-center justify-between">
                <span className="text-xs text-slate-600">{label}</span>
                <span className={`text-xs font-display font-bold tabular-nums ${accent || ''}`}>{inrCompact(value || 0)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cost breakdown card */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="kpi-overline">Cost Breakdown</div>
            <div className="font-display font-extrabold text-sm text-red-600 tabular-nums">{inrCompact(costs.total_cost)}</div>
          </div>
          <div className="divide-y divide-slate-100">
            {Object.entries(COST_LABELS).map(([key, label]) => {
              const val = costs[key] || 0;
              const Icon = COST_ICONS[key] || Package;
              const total = costs.total_cost || 1;
              return (
                <div key={key} className="px-4 py-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-600 flex items-center gap-1.5"><Icon size={11} className="text-slate-400" />{label}</span>
                    <span className="text-xs font-display font-bold tabular-nums">{inrCompact(val)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#3b4872] rounded-full" style={{ width: `${Math.min((val / total) * 100, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Margin card */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline">Gross Profit</div>
          <div className={`font-display text-2xl font-extrabold tracking-tight mt-1 ${(margin.gross_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{inrCompact(margin.gross_profit || 0)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline">Gross Margin</div>
          <div className={`font-display text-2xl font-extrabold tracking-tight mt-1 ${(margin.gross_margin_pct || 0) >= 15 ? 'text-emerald-700' : (margin.gross_margin_pct || 0) >= 5 ? 'text-amber-600' : 'text-red-600'}`} data-testid="pnl-margin-pct">{margin.gross_margin_pct || 0}%</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline">RA Bills Raised</div>
          <div className="font-display text-2xl font-extrabold tracking-tight mt-1">{inrCompact(rev.ra_bills_raised || 0)}</div>
          <div className="text-[11px] text-slate-500 mt-1">vs {inrCompact(rev.earned_revenue || 0)} earned</div>
        </div>
      </div>

      {/* EVM card */}
      <EVMCard evm={pnl.evm} />

      {/* Budget vs Actual */}
      {budgetData.length > 0 && (
        <div>
          <div className="kpi-overline mb-2">Budget vs Actual — by cost head</div>
          <ResponsiveContainer width="100%" height={Math.max(budgetData.length * 40, 200)}>
            <BarChart data={budgetData} layout="vertical">
              <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `${(v / 10000000).toFixed(0)}Cr`} />
              <YAxis type="category" dataKey="head" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={130} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} formatter={v => inrCompact(v)} />
              <Bar dataKey="Budgeted" fill="#2F3E46" radius={[0, 2, 2, 0]} />
              <Bar dataKey="Actual" fill="#3b4872" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tender Financial */}
      {pnl.tender_financial && (pnl.tender_financial.pbg_amount > 0 || pnl.tender_financial.retention_pct > 0) && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline mb-3">Tender / Contract Terms</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {pnl.tender_financial.pbg_amount > 0 && <div><div className="text-slate-400">PBG Amount</div><div className="font-display font-bold">{inrCompact(pnl.tender_financial.pbg_amount)}</div><div className="text-slate-400 text-[10px]">Valid till {formatDate(pnl.tender_financial.pbg_validity_date)}</div></div>}
            {pnl.tender_financial.abg_amount > 0 && <div><div className="text-slate-400">ABG Amount</div><div className="font-display font-bold">{inrCompact(pnl.tender_financial.abg_amount)}</div><div className="text-slate-400 text-[10px]">Valid till {formatDate(pnl.tender_financial.abg_validity_date)}</div></div>}
            {pnl.tender_financial.retention_pct > 0 && <div><div className="text-slate-400">Retention %</div><div className="font-display font-bold">{pnl.tender_financial.retention_pct}%</div></div>}
            {pnl.tender_financial.payment_terms_text && <div className="md:col-span-2"><div className="text-slate-400">Payment Terms</div><div className="text-slate-700">{pnl.tender_financial.payment_terms_text}</div></div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Aging Panel ───────────────────────────────────────────────────────────────
function AgingPanel({ kind, data, title }) {
  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  const colors = { '0-30': 'bg-emerald-500', '31-60': 'bg-amber-400', '61-90': 'bg-indigo-500', '90+': 'bg-red-500' };
  const maxBucket = Math.max(...Object.values(data.buckets), 1);
  const isAR = kind === 'ar';
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(data.buckets).map(([b, v]) => (
          <div key={b} className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="kpi-overline">{b} days</div>
            <div className="font-display text-xl font-extrabold tracking-tight mt-1 mb-2">{inrCompact(v)}</div>
            <div className="h-1 bg-slate-100 rounded-sm overflow-hidden">
              <div className={`h-full ${colors[b]}`} style={{ width: `${(v / maxBucket) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      {data.by_category && Object.keys(data.by_category).length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <CategoryBreakdown data={data.by_category} label={isAR ? 'Receivables by Category' : 'Payables by Category'} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-y-auto max-h-[70vh]">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="kpi-overline">{title}</div>
            <h3 className="font-display font-bold text-base mt-1">{data.rows.length} outstanding · total {inrCompact(data.total)}</h3>
          </div>
        </div>
        <table className="w-full dense-table">
          <thead><tr>
            <th>{isAR ? 'Bill / Client' : 'Invoice / Vendor'}</th>
            <th className="text-right">Amount</th>
            <th>Due Date</th>
            <th className="text-right">Days Overdue</th>
            <th>Bucket</th>
            <th>Category</th>
            <th>Status</th>
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan="7" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No {isAR ? 'AR' : 'AP'} outstanding</td></tr>}
            {data.rows.map(r => (
              <tr key={r.id}>
                <td>
                  <div className="font-mono text-xs">{r.invoice_no || r.bill_no}</div>
                  <div className="text-[11px] text-slate-500">{r.vendor || r.client}</div>
                </td>
                <td className="text-right tabular-nums font-display font-bold">{inrCompact(r.amount)}</td>
                <td className="text-xs">{formatDate(r.due_date)}</td>
                <td className={`text-right tabular-nums font-display font-bold ${r.days_overdue > 60 ? 'text-red-600' : r.days_overdue > 30 ? 'text-amber-600' : 'text-emerald-700'}`}>{r.days_overdue}</td>
                <td><span className={`tag ${colors[r.bucket]} text-white border-0`}>{r.bucket}</span></td>
                <td><span className="tag bg-slate-100 text-slate-700 capitalize">{(r.category || 'other').replace(/_/g, ' ')}</span></td>
                <td><span className={`tag ${statusColor(r.status)}`}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Forecast Panel ────────────────────────────────────────────────────────────
function ForecastPanel({ forecast }) {
  if (!forecast) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  const negativeWeeks = forecast.weeks.filter(w => w.cumulative < 0).length;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KCard label="Opening Balance" value={inrCompact(forecast.opening_balance)} sub="Today" />
        <KCard label="Total Inflow" value={inrCompact(forecast.weeks.reduce((s, w) => s + w.inflow, 0))} sub="Projected over 13 weeks" />
        <KCard label="Total Outflow" value={inrCompact(forecast.weeks.reduce((s, w) => s + w.outflow, 0))} sub="Projected over 13 weeks" />
        <KCard label="Closing Position" value={inrCompact(forecast.weeks[forecast.weeks.length - 1].cumulative)}
          sub={negativeWeeks > 0 ? `⚠ ${negativeWeeks} weeks below zero` : 'Healthy'}
          icon={negativeWeeks > 0 ? AlertCircle : TrendingUp}
          accent={negativeWeeks > 0 ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50'} />
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
        <div className="kpi-overline mb-3">Cumulative cash position with weekly net</div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={forecast.weeks}>
            <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="week" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `${(v / 10000000).toFixed(0)}Cr`} />
            <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} formatter={v => inrCompact(v)} />
            <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" />
            <Bar dataKey="net" fill="#3b4872" name="Weekly net" radius={[2, 2, 0, 0]} />
            <Line type="monotone" dataKey="cumulative" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 4, fill: '#0F172A' }} name="Cumulative cash" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-y-auto max-h-[70vh]">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Week</th><th>Starting</th><th className="text-right">Inflow</th><th className="text-right">Outflow</th><th className="text-right">Net</th><th className="text-right">Cumulative</th>
          </tr></thead>
          <tbody>
            {forecast.weeks.map(w => (
              <tr key={w.week} className={w.cumulative < 0 ? 'bg-red-50/30' : ''}>
                <td className="font-display font-bold">{w.week}</td>
                <td className="text-xs">{formatDate(w.starting)}</td>
                <td className="text-right tabular-nums text-emerald-700 font-mono">{inrCompact(w.inflow)}</td>
                <td className="text-right tabular-nums text-red-600 font-mono">({inrCompact(w.outflow)})</td>
                <td className={`text-right tabular-nums font-display font-bold ${w.net < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{w.net < 0 ? '(' : ''}{inrCompact(Math.abs(w.net))}{w.net < 0 ? ')' : ''}</td>
                <td className={`text-right tabular-nums font-display font-extrabold ${w.cumulative < 0 ? 'text-red-600' : ''}`}>{inrCompact(w.cumulative)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
